import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, Info, KeyRound, CheckCircle2, Trash2, ExternalLink, ShieldCheck } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button, Input, Card, Badge, Spinner } from "@/components/kit";
import DeviceDisconnectWarning from "@/components/DeviceDisconnectWarning";
import WaSafetyCard from "@/components/WaSafetyCard";

const STEPS = [
  "Daftar / login ke akun Fonnte di fonnte.com, lalu hubungkan nomor WhatsApp kamu (scan QR di dashboard Fonnte).",
  "Buka menu Device → pilih device → salin Token device (atau Account Token).",
  "Tempel token di bawah ini lalu klik Hubungkan.",
];

export default function Devices() {
  const [data, setData] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/wa/account").then((r) => setData(r.data)).catch(() => setData({ connected: false }));
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (token.trim().length < 8) { toast.error("Token Fonnte tidak valid"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/wa/connect", { token: token.trim() });
      toast.success("Akun Fonnte terhubung!");
      setToken(""); setData({ connected: true, device: data.device });
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    try { await api.post("/wa/disconnect"); toast.success("Akun diputuskan"); setData({ connected: false }); }
    catch { toast.error("Gagal memutuskan"); }
  };

  const d = data?.device || {};

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-50 grid place-items-center"><Smartphone className="w-6 h-6 text-emerald-600" /></div>
        <div>
          <span className="inline-flex items-center gap-3"><span className="w-10 h-10 rounded-xl bg-sky-100 grid place-items-center shrink-0"><Smartphone className="w-5 h-5 text-sky-600" /></span><h1 className="font-display text-3xl font-extrabold">Koneksi WhatsApp (BYOK)</h1></span>
          <p className="text-slate-500 mt-0.5 text-sm">Hubungkan akun Fonnte milikmu sendiri untuk WA Blast.</p>
        </div>
      </div>

      {/* Why BYOK */}
      <Card className="p-5 bg-sky-50 border-sky-200" data-testid="byok-info">
        <div className="flex gap-3">
          <ShieldCheck className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
          <div className="text-sm text-sky-900 space-y-1">
            <p className="font-semibold">Kenapa pakai BYOK (Bring Your Own Key)?</p>
            <p>Blast WhatsApp dikirim langsung dari <b>nomor & kuota Fonnte milikmu sendiri</b> — bukan gateway bersama. Artinya: <b>tanpa batas kuota dari kami</b> (mengikuti paket Fonnte kamu), nomor pengirim adalah milikmu, dan data tetap privat.</p>
          </div>
        </div>
      </Card>

      {data === null ? <Spinner className="w-7 h-7 mx-auto my-12" /> : data.connected ? (
        <>
          <DeviceDisconnectWarning device={d} onRefresh={load} onReconnected={load} />
          <Card className="p-6" data-testid="wa-connected">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold flex items-center gap-2">
                <CheckCircle2 className={`w-5 h-5 ${d.connected ? "text-emerald-500" : "text-slate-300"}`} />
                {d.connected ? "Terhubung" : "Terkonek ke Fonnte (perangkat offline)"}
              </h3>
              <Button variant="danger" size="sm" onClick={disconnect} data-testid="wa-disconnect"><Trash2 className="w-4 h-4" /> Putuskan</Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ["Nama Device", d.name || "—"],
                ["Nomor", d.device || "—"],
                ["Status", <span key="s" className={d.connected ? "text-emerald-600" : "text-rose-600 font-semibold"}>{d.status || "—"}</span>],
                ["Sisa Kuota Pesan", d.quota != null ? Number(d.quota).toLocaleString("id-ID") : "—"],
                ["Pesan Terkirim", d.messages != null ? Number(d.messages).toLocaleString("id-ID") : "—"],
                ["Paket", d.package || "—"],
                ["Masa Aktif", d.expired || "—"],
              ].map(([l, v]) => (
                <div key={l} className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400">{l}</p>
                  <p className="font-semibold text-slate-800 mt-0.5 break-words" data-testid="wa-device-field">{v}</p>
                </div>
              ))}
            </div>
          </Card>
          <WaSafetyCard />
        </>
      ) : (
        <Card className="p-6 space-y-5" data-testid="wa-connect-form">
          <div>
            <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Info className="w-4 h-4 text-sky-500" /> Tutorial Singkat</h3>
            <ol className="space-y-2">
              {STEPS.map((s, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-600">
                  <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs font-bold grid place-items-center shrink-0">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
            <a href="https://fonnte.com" target="_blank" rel="noreferrer" className="text-sm text-sky-600 font-semibold inline-flex items-center gap-1 mt-3">
              Buka Fonnte <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="pt-4 border-t border-slate-100">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Token Fonnte</label>
            <div className="flex gap-2 mt-2">
              <Input data-testid="fonnte-token-input" placeholder="Tempel token Fonnte di sini" value={token} onChange={(e) => setToken(e.target.value)} className="flex-1" />
              <Button data-testid="fonnte-connect" onClick={connect} loading={busy}>Hubungkan</Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
